import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const PasswordGate = ({ overline, title, subtitle, onSubmit, testIdPrefix }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onSubmit(password);
    } catch {
      setError("That password doesn't open these doors. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-6" data-testid={`${testIdPrefix}-gate`}>
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{
          backgroundImage:
            "url(/hero-bg.jpg)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md border border-white/10 bg-black/60 p-10 backdrop-blur-xl md:p-14"
      >
        <div className="mb-10 flex items-center gap-3">
          <Lock className="h-4 w-4 text-gold" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.3em] text-[#A1A19A]">{overline}</span>
        </div>

        <h1 className="font-display text-4xl leading-tight tracking-tight text-[#F5F5F0] md:text-5xl">
          {title}
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-[#A1A19A]">{subtitle}</p>

        <form onSubmit={submit} className="mt-10 space-y-6">
          <Input
            data-testid={`${testIdPrefix}-password-input`}
            type="password"
            autoComplete="current-password"
            placeholder="Enter the secret password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-14 rounded-none border-white/15 bg-white/5 px-5 text-base text-[#F5F5F0] placeholder:text-[#666666] focus-visible:ring-1 focus-visible:ring-gold"
          />
          {error && (
            <p data-testid={`${testIdPrefix}-error`} role="alert" className="text-sm text-[#E07070]">
              {error}
            </p>
          )}
          <Button
            data-testid={`${testIdPrefix}-submit-button`}
            type="submit"
            disabled={busy}
            className="group h-14 w-full rounded-none bg-gold text-xs font-medium uppercase tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-gold-hover"
          >
            {busy ? "Checking…" : "Step Inside"}
            <ArrowRight className="ml-3 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </Button>
        </form>
      </motion.div>
    </div>
  );
};
